import * as React from 'react';
import { styled } from 'styled-components';

import { ListView } from '@longline/aqua-ui/controls/ListView';
import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { SVG } from '@longline/aqua-ui/svg';
import { Message } from '@longline/aqua-ui/containers/Message';
import { Icon } from '@longline/aqua-ui/controls/Icon';

import { ISystemLayer } from '../../../api/models';
import { LayerUtils } from '../../../util/LayerUtils';
import { ELayerType } from '../../../types/ELayerType';
import { LayerManagerWarning } from './LayerManagerWarning';
import { LayerManagerInput } from './LayerManagerInput';

interface IProps {
  layers: ISystemLayer[];
  onAdd: () => void;
  onDelete: (layer: ISystemLayer) => void;
  onEdit: (layer: ISystemLayer, name: string) => void;
}

const LayerManager = (props: IProps) => {
  // Which item is being edited?
  const [editing, setEditing] = React.useState<ISystemLayer | null>(null);

  // Collect any warnings about missing layers:
  const getWarnings = () => {
    const bathymetryCount = (props.layers || []).filter(l => l.type == ELayerType.Depth).length;
    const currentSpeedCount = (props.layers || []).filter(l => l.type == ELayerType.CurrentSpeed).length;
    const warnings = [];
    if(bathymetryCount == 0) warnings.push(<LayerManagerWarning key="no-bathymetry">A system requires a <strong>bathymetry</strong> dataset.</LayerManagerWarning>);
    if(bathymetryCount >  1) warnings.push(<LayerManagerWarning key="multiple-bathymetry">A system can have only one <strong>bathymetry</strong> dataset.</LayerManagerWarning>);
    if(currentSpeedCount == 0) warnings.push(<LayerManagerWarning key="no-current">A system requires a <strong>current speed</strong> dataset.</LayerManagerWarning>);
    return warnings;
  }
  const warnings = getWarnings();

  return (
    <div style={{ height: '100%', display: "flex", flexDirection: 'column' }}>
      <div style={{ position: 'relative', height: '100%' }}>
        <ListView
          dark
          data={props.layers}
          singular="dataset"
          plural="datasets"
          shadow
          hover
          total
          rowHeight={56}
          noData={<Message.NoData margin>There are no datasets yet.</Message.NoData>}
        >
          <ListView.Column active width={2} name="Name">{item =>
            editing === item
            ? <LayerManagerInput value={item.name} onEdit={(v) => props.onEdit(item, v)} onClose={() => setEditing(null)}/>
            : <ItemRow>
                <Name>{item.name || "(no name)"}</Name>
                <IconWrapper>
                  <Icon size="large" url={SVG.Icons.Edit} onClick={() => setEditing(item)} />
                </IconWrapper>
              </ItemRow>
          }</ListView.Column>
          <ListView.Column active width={1} name="Type">{item => LayerUtils.layerTypeToDefinition(item.type).name}</ListView.Column>
          <ListView.Column active width="84px" align="right" name="">{item => <div style={{ padding: "2px" }}><SecondaryButton title="Remove" negative icon={SVG.Icons.Minus} onClick={() => props.onDelete(item)}/></div>}</ListView.Column>
        </ListView>
        <ButtonHolder>
          <SecondaryButton positive title="Add dataset" icon={SVG.Icons.Plus} onClick={props.onAdd}/>
        </ButtonHolder>
      </div>
      {warnings.length > 0 && <WarningList>
        {getWarnings()}
      </WarningList>}
    </div>
  )
}

const WarningList = styled.div`
  background-color: ${p => p.theme.colors.neutral[10]};
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 16px;
`

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const Name = styled.div`
  flex: 1;
  min-width: 0; /* required for text-overflow in flex containers */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconWrapper = styled.div`
  flex: 0 0 30px;
  display: flex;
  justify-content: flex-end;
`;

const ButtonHolder = styled.div`
  position: absolute;
  z-index: 100;
  right: 16px;
  bottom: 16px;
`

export { LayerManager }
